/**
 * FlowAI Event-Queue — letzte 200 Events.
 *
 * @page /flowai/events
 * @since v2.484.0
 */
import { useEffect, useState } from 'react';
import { PageHeader, Card, Badge } from '../../components/ui';
import { IconCases } from '../../components/Icons';
import FlowAIBreadcrumb from '../../components/flowai/FlowAIBreadcrumb';

const API = import.meta.env.VITE_API_URL || '';

function statusTone(s) {
  if (s === 'done' || s === 'matched_playbook') return 'success';
  if (s === 'failed') return 'danger';
  if (s === 'no_match') return 'default';
  return 'default';
}

function formatEventDate(raw) {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('de-AT', { timeZone: 'Europe/Vienna', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function FlowAIEvents() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  async function load() {
    const r = await fetch(`${API}/api/flowai/events?limit=200`, { credentials: 'include' });
    const d = await r.json().catch(() => ({}));
    setRows(Array.isArray(d) ? d : (d?.events ?? []));
  }
  useEffect(() => { load(); }, []);

  async function emitTest() {
    setBusy(true);
    await fetch(`${API}/api/flowai/internal/test-event`, { method: 'POST', credentials: 'include' });
    setBusy(false);
    setTimeout(load, 600);
  }

  return (
    <div className="p-6">
      <FlowAIBreadcrumb trail={[{ label: 'Event-Queue' }]} />
      <PageHeader
        title="Event-Queue"
        subtitle="Inbound-Events vom System"
        actions={
          <div className="flex gap-2">
            <button onClick={emitTest} disabled={busy} className="px-3 py-1.5 border border-[var(--border-subtle,#E5E7EB)] rounded text-sm">Test-Event</button>
            <button onClick={load} className="px-3 py-1.5 border border-[var(--border-subtle,#E5E7EB)] rounded text-sm">Aktualisieren</button>
          </div>
        }
      />
      <Card padding="sm">
        <div className="divide-y divide-[var(--border-subtle,#E5E7EB)] max-h-[80vh] overflow-y-auto">
          {rows.map((e) => (
            <div key={e.id} className="p-3 text-[12px]">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[var(--text-tertiary,#6B7280)]">#{e.id}</span>
                <span className="font-medium">{e.event_type}</span>
                <Badge variant={statusTone(e.status)}>{e.status}</Badge>
                <span className="ml-auto text-[var(--text-tertiary,#6B7280)]">{formatEventDate(e.created_at)}</span>
              </div>
              {e.payload && (
                <pre className="mt-1 bg-[var(--bg-inset,#F8F9FC)] p-2 rounded text-[11px] overflow-x-auto">{JSON.stringify(e.payload, null, 2)}</pre>
              )}
            </div>
          ))}
          {rows.length === 0 && (
            <div className="p-12 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[var(--bg-inset,#F8F9FC)] mb-3">
                <IconCases className="w-6 h-6 text-[var(--text-tertiary,#6B7280)]" />
              </div>
              <div className="text-sm font-medium mb-1">Keine Events</div>
              <div className="text-[12px] text-[var(--text-tertiary,#6B7280)]">Sobald Cases/Sales-Module Events triggern, erscheinen sie hier.</div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
